import { toPng } from 'html-to-image';
import { getNodesBounds, getViewportForBounds } from 'reactflow';
import { clsx } from 'clsx';
import { log } from '@/logger/logger.ts';
import { POSTER_WIDTH, POSTER_HEIGHT } from '@/consts/recipe-poster';
import { ModelItem } from '@/state/nodes/nodes.types';
import { FlowMode } from '@/enums/flow-modes.enum';
import { NodeType } from '@/enums/node-type.enum';
import { shouldApplyStoredBoxSize, MEDIA_VIEWPORT_DIMENSION_NODE_TYPES } from '@/consts/node-ui.consts';
import { color } from '@/colors';
import { Connection } from './FlowComponents/FlowTour/ConnectionContext';
import type { Instance, Node as ReactFlowNode } from 'reactflow';
import type { CustomGroupData, Node } from '@/types/node';
import type { NodeDimState } from './Drawer/useAppBuilderState';

const logger = log.getLogger('utils');

function captureVideoFrame(videoElement: HTMLVideoElement) {
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("Couldn't get canvas 2d context");
  videoElement.crossOrigin = 'anonymous';
  ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/png');
}

export async function createPoster({
  getNodes,
  posterWidth = POSTER_WIDTH,
  posterHeight = POSTER_HEIGHT,
}: {
  posterWidth: number;
  posterHeight: number;
  getNodes: Instance.GetNodes<ReactFlowNode>;
}) {
  const nodesBounds = getNodesBounds(getNodes());
  const transform = getViewportForBounds(nodesBounds, POSTER_WIDTH, POSTER_HEIGHT, 0.1, 3);

  const videos = document.querySelectorAll('video');
  const videoReplacements = new Map<HTMLVideoElement, HTMLImageElement>();

  videos.forEach((video) => {
    const frameDataURL = captureVideoFrame(video);
    const imageElement = new Image();
    imageElement.src = frameDataURL;
    imageElement.style.width = `${video.offsetWidth}px`;
    imageElement.style.height = `${video.offsetHeight}px`;
    if (video.parentNode) {
      video.parentNode.replaceChild(imageElement, video);
      videoReplacements.set(video, imageElement);
    }
  });

  try {
    const viewport = document.querySelector('.react-flow__viewport') as HTMLElement;
    if (!viewport) throw new Error('Viewport not found');
    const dataUrl = await toPng(viewport, {
      backgroundColor: color.Black100,
      width: posterWidth,
      height: posterHeight,
      skipFonts: true,
      style: {
        width: posterWidth.toString(),
        height: posterHeight.toString(),
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
      },
      pixelRatio: 1,
    });

    return dataUrl;
  } catch (err) {
    logger.error('Failed to create poster image', err);
  } finally {
    videoReplacements.forEach((imageElement, video) => {
      if (!imageElement.parentNode) throw new Error('image parent not found');
      imageElement.parentNode.replaceChild(video, imageElement);
    });
  }
}

export const getOppositeHandleKeyForConnection = (
  side: Connection['handleSide'],
): keyof Pick<ModelItem, 'inputTypes' | 'outputTypes'> => (side && side === 'source' ? 'inputTypes' : 'outputTypes');

/**
 * Core "is the canvas interactable right now" check: no node editor or media
 * gallery open, and we're in Workflow (not App) mode.
 */
export const isBaseFlowContextAllowed = (
  isEditorOpen: boolean,
  isGalleryOpen: boolean,
  flowViewingMode: FlowMode,
): boolean => !isEditorOpen && !isGalleryOpen && flowViewingMode === FlowMode.Workflow;

export const isRecipeInteractionAllowed = (
  isEditorOpen: boolean,
  isGalleryOpen: boolean,
  role: string,
  flowViewingMode: FlowMode,
  isMenuOpened: boolean,
) => isBaseFlowContextAllowed(isEditorOpen, isGalleryOpen, flowViewingMode) && role !== 'guest' && !isMenuOpened;

export function toReactFlowNode(
  node: Node,
  highlightedNodeIds: string[],
  getEffectiveIsLocked: (nodeId: string) => boolean,
  suppressedSelectionNodeId: string | null,
  appBuilderState: NodeDimState,
  isDiagonalResizeEnabled: boolean,
): ReactFlowNode {
  const effectiveIsLocked = getEffectiveIsLocked(node.id);
  // Moving is the one thing the read-only Tool tab allows, and a lock stops it there as on Canvas.
  // `undefined` there, not `true`: React Flow then follows `nodesDraggable`, so a guest still can't.
  const isToolTab = appBuilderState.isToolTabReadOnly;
  const baseNode: ReactFlowNode = {
    ...node,
    draggable: effectiveIsLocked ? false : isToolTab ? undefined : ((node as ReactFlowNode).draggable ?? undefined),
    // Picking an output is a mode, not a selection. A click chooses a node; it must not also select it.
    ...(appBuilderState.isPickingOutput ? { selectable: false } : {}),
  };
  const isHighlighted = highlightedNodeIds.includes(node.id);
  // Dimmed only while the builder is picking an output, and then it means "can't be picked". At rest
  // the Tool tab draws the canvas as the Canvas tab does; the frames alone say what is in the tool.
  // A group's fill is its own colour. Washing the group node paints that fill too, and the nodes
  // inside already wash on their own.
  const isDimmed =
    node.type !== NodeType.CustomGroup &&
    appBuilderState.isCanvasInAppBuilderMode &&
    appBuilderState.isPickingOutput &&
    !appBuilderState.isEligibleOutputNode(node.id);
  const appBuilderClassName = clsx(baseNode.className, isDimmed && 'app-builder-dimmed');
  const className = clsx(appBuilderClassName, {
    highlighted: isHighlighted,
  });

  if (node.type === NodeType.CustomGroup) {
    const nodeData = node.data as CustomGroupData;
    return {
      ...baseNode,
      width: nodeData.width,
      height: nodeData.height,
      draggable: effectiveIsLocked ? false : isToolTab ? undefined : true,
      selectable: appBuilderState.isPickingOutput ? false : node.id !== suppressedSelectionNodeId,
      dragHandle: '.custom-group-drag-handle',
      style: {
        width: nodeData.width,
        height: nodeData.height,
        pointerEvents: !isDimmed && node.selected ? 'auto' : 'none', // Pass through events when not selected to allow selection box
      },
      className: appBuilderClassName || undefined,
    };
  }

  const usesViewportDimensions = node.type !== undefined && MEDIA_VIEWPORT_DIMENSION_NODE_TYPES.has(node.type);
  const shouldApplyStoredBoxSizeForNode = shouldApplyStoredBoxSize(node.type, isDiagonalResizeEnabled);

  if (shouldApplyStoredBoxSizeForNode) {
    const { width, height, nodeWidth, nodeHeight } = (node.data ?? {}) as {
      width?: number;
      height?: number;
      nodeWidth?: number;
      nodeHeight?: number;
    };
    if (!usesViewportDimensions || isDiagonalResizeEnabled) {
      const resolvedWidth = usesViewportDimensions ? nodeWidth : width;
      const resolvedHeight = usesViewportDimensions ? nodeHeight : height;
      if (resolvedWidth !== undefined) baseNode.width = resolvedWidth;
      // With diagonal resizing, the shell applies the saved height according to its
      // content sizing mode. React Flow's height must remain the measured shell height:
      // a media node can shrink or grow when its selected asset changes at a fixed width.
      if (!isDiagonalResizeEnabled && resolvedHeight !== undefined) baseNode.height = resolvedHeight;
    }
  }

  return {
    ...baseNode,
    className,
  };
}

export const isPasteFromEditableElement = (event: ClipboardEvent): boolean => {
  // If the paste originates from an input/textarea/contenteditable element (e.g. Import node input, import model node etc.),
  // we should not treat it as a canvas paste
  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('input, textarea, [contenteditable="true"]'));
};

/**
 * Whether the currently-focused element is an editable field. Used by the Figma
 * embed message handlers (which have no originating `event.target`) to mirror the
 * focus guard in `Flow.jsx`'s `handleKeyDown`, so native Edit-menu commands
 * forwarded from the desktop shell don't hijack in-field editing.
 */
export const isEditableElementFocused = (includeContentEditable: boolean): boolean => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) {
    return false;
  }
  if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') {
    return true;
  }
  return includeContentEditable && active.isContentEditable;
};

/**
 * Whether there is an active, non-empty text selection — either a ranged
 * selection inside the focused `<input>`/`<textarea>`, or a non-collapsed
 * document selection (e.g. contentEditable / plain page text).
 */
export const hasActiveTextSelection = (): boolean => {
  const active = document.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    const { selectionStart, selectionEnd } = active;
    if (selectionStart != null && selectionEnd != null && selectionStart !== selectionEnd) {
      return true;
    }
  }
  return (window.getSelection()?.toString().length ?? 0) > 0;
};
